import React from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { OverdueAlert } from "../../../../types/onboardingAnalytics";
import { stateLabel } from "../../../../utils/onboardingStates";
import { OnboardingState } from "../../../../types/onboarding";
import { formatCount } from "../../../../utils/analyticsFormat";

interface OverdueAlertsPanelProps {
  alerts: OverdueAlert[];
}

// Long enough to scroll rather than stretch the page: the endpoint is unpaged
// and returns every overdue prospect at once.
const SCROLL_HEIGHT = "22rem";

/**
 * The prospects an agent should chase today.
 *
 * The backend decides what "overdue" means, per state, and the thresholds are
 * not uniform — FORM_SUBMITTED is 3 days while OCPP_CREDENTIALS_SENT is 14,
 * because a customer wiring up a wallbox is legitimately slower than a form
 * waiting to be picked up. The screen states the rule so the list does not look
 * arbitrary, but does not re-implement it.
 *
 * Overdue only: the epic also described reminder and failed-notification alerts,
 * and neither exists in the API. The heading must not imply otherwise (FR-009).
 */
const OverdueAlertsPanel: React.FC<OverdueAlertsPanelProps> = ({ alerts }) => {
  const navigate = useNavigate();

  const daysBody = (alert: OverdueAlert) => (
    <Tag
      severity={alert.daysStuck >= 14 ? "danger" : "warning"}
      value={`${formatCount(alert.daysStuck)} ${
        alert.daysStuck === 1 ? "day" : "days"
      }`}
    />
  );

  // Never the raw identifier, even for a state this build has no label for —
  // stateLabel falls back to the identifier only as a last resort, and that is
  // still better than rendering nothing (FR-029, FR-032).
  const stageBody = (alert: OverdueAlert) =>
    stateLabel(alert.state as OnboardingState);

  return (
    <DataTable
      value={alerts}
      dataKey="prospectId"
      scrollable
      scrollHeight={SCROLL_HEIGHT}
      selectionMode="single"
      onSelectionChange={(e) =>
        navigate(`/onboarding/prospects/${(e.value as OverdueAlert).prospectId}`)
      }
      className="p-datatable-sm"
      aria-label="Overdue prospects"
    >
      <Column field="name" header="Prospect" />
      <Column header="Stage" body={stageBody} />
      <Column header="Stuck for" body={daysBody} style={{ width: "9rem" }} />
    </DataTable>
  );
};

export default OverdueAlertsPanel;
